// Vendor Item Purchase Request - This is the Mobile Side (Part 1)
// VendorItemRequest.js is Desktop Side (Part 2)
import React, { useContext, useState, useEffect } from 'react';
import {
   Button, ButtonGroup, Card, CardBody, CardTitle, Col, Input,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { toast } from "react-toastify";
import { ServiceThreadSpinner, VendorPallet } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, UserContext, storage } from '../utils';

export default function VendorItemScan() {
   const PALLET_MODEL = { Barcode: "", Item: "", ItemDesc: "", NetWeight: 0, Pallet: "", PoNum: "", SiteId: "" };
   const { currentUser } = useContext(UserContext);
   const [barcode, setBarcode] = useState(null);
   const [item, setItem] = useState("");                  // Item provided by first scan
   const [itemWithDesc, setItemWithDesc] = useState("");  // Item with description provided by first scan
   const [loaded, setLoaded] = useState(false);
   const [manualEntry, setManual] = useState(null);
   const [model, setModel] = useState({});                // Current pallet in process
   const [open, setOpen] = useState(null);                // Show barcode scanner dialog
   const [pallets, setPallets] = useState([]);            // List of pallets in process
   const [showWgt, setShowWgt] = useState(false);         // Show weight entry modal
   const [site, setSite] = useState("");                  // SiteId provided by first scan
   const [spin, setSpinner] = useState(false);
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;

   useEffect(() => {
      async function load() {
         await sleep(80);  // allows time for input to be recognized for focus
         if (!loaded) {
            inputFocus("searchInput");
         }
         else if (showWgt) {
            inputFocus("netWt");
         }
      }
      load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      // if device has built-in scanner see if a scan was completed
      if (hasScanner && barcode && !manualEntry) {
         if (constants.GetBarcodeType(barcode) !== constants.BARCODE_TYPE.InventoryTransfer) {
            toast.warn("Invalid IV Barcode");
            return;
         }
         setLoaded(false);
         getLabel();
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      if (constants.GetBarcodeType(barcode) !== constants.BARCODE_TYPE.InventoryTransfer) {
         toast.warn("Invalid IV Barcode");
         return;
      }
      setLoaded(false);
      getLabel();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   function addPallet() {
      pallets.push(model);
      setShowWgt(false);
      setModel({});
      console.log(pallets);
   }

   function getLabel() {
      if (barcode) {
         // If a QR code was scanned then parse out just the barcode from the data            
         var bc = constants.GetRealBarcode(barcode);
         if (bc.substring(0, 2) !== "IV") {
            toast.error("Invalid IV Barcode");
            return;
         }

         // Ensure no duplicates
         var itmIdx = getObjectByKey(pallets, 'barcode', bc);
         if (itmIdx !== null && itmIdx > -1) {
            toast.error("Barcode was already scanned");
            return;
         }

         // Set data payload
         let payload = Object.assign({}, PALLET_MODEL);
         payload.barcode = bc;
         payload.Item = item;
         payload.SiteId = site;

         setSpinner(true);
         api
            .post(`Transfer/GetConsignedItem`, payload)
            .then(response => {
               if (response.data.success) {
                  setModel(response.data.model);
                  if (item === "") {
                     setItem(response.data.model.item);
                     setItemWithDesc(response.data.model.itemWithDesc);
                     setSite(response.data.model.siteId);
                  }
                  setLoaded(true);
                  setShowWgt(true);
               }
               else {
                  toast.error(response.data.message);
                  setLoaded(false);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               setLoaded(false);
            })
            .finally(() => {
               setBarcode(null);
               setSpinner(false);
            });
      }
   }

   function getObjectByKey(array, key, value) {
      // Return the index of the key/value pair
      for (var i = 0; i < array.length; i++) {
         if (array[i][key] === value) {
            return i;
         }
      }
      return null;
   }

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      // On key press for search input field
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(barcode) {
      // On change of search input field
      setBarcode(barcode);

      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      inputFocus("searchInput");
   }

   function onChange(field, value) {
      if (field === "netWeight" && value <= 0) {
         toast.warn("Net Weight must be > 0");
         return;
      }        

      let changes = Object.assign({}, model);
      changes[field] = value;
      setModel(changes);
   }

   function onToggle() {
      inputFocus("searchInput");
   }

   function reset() {
      setBarcode(null);    // clear any barcode previously scanned
      setItem("");         // clear item in process
      setItemWithDesc(""); // clear item with description
      setLoaded(false);    // starting over, must scan or enter a barcode
      setModel({});        // clear any previous case model
      setOpen(null);       // hide barcode scanner dialog
      setPallets([]);      // clear list of pallets in process
      setShowWgt(false);   // hide weight entry
      setSite("");         // clear site in process
      setSpinner(false);   // hide spinner
   }

   function resetModel() {
      setBarcode(null);    // clear any barcode previously scanned
      setModel({});        // clear any previous case model
      setOpen(null);       // hide barcode scanner dialog
      setShowWgt(false);   // hide weight entry
      setSpinner(false);   // hide spinner
   }

   function saveRequest() {
      let payload = {};
      payload.Labels = pallets;

      setSpinner(true);
      api
         .post(`Transfer/SaveVendorPurchase`, payload)
         .then(response => {
            if (response.data.success) {
               toast.success('Purchase request successfully created');
               reset();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   return (
      <>
         <DesktopContainer screenName="Consigned Item Request">
            <ServiceThreadSpinner on={spin}>
               <div>
                  <Row><Col className="h6 fw-bold" xs="12">To begin scan an inventory label.</Col></Row>
                  <Row><Col><hr className="mb-0 my-1" /></Col></Row>
                  <br />
                  {!hasScanner && isMobile ?
                     <div>
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Button title="scan entry" className="shredButton w-100 btn-md" onClick={() => setOpen(1)}>
                                 Scan Barcode
                              </Button>
                           </Col>
                        </Row>
                        <Row className="lineheight8">&nbsp;</Row>
                     </div>
                     : null
                  }
                  <div>
                     <Row className="justify-content-center">
                        <Col xs="12">
                           <Button title="manual entry" className="shredButton w-100 btn-md" onClick={() => setManual(1)}>
                              Manual Entry
                           </Button>
                        </Col>
                     </Row>
                     {isMobile ?
                        <div>
                           <Row className="lineheight8">&nbsp;</Row>
                           <Row className="justify-content-center">
                              <Col xs="12">
                                 <Input
                                    name="searchInput"
                                    placeholder="Focus here to scan barcode"
                                    className="h-100"
                                    onChange={(e) => lookup(e.target.value.toUpperCase())}
                                 />
                              </Col>
                           </Row>
                        </div>
                        : null
                     }
                  </div>
               </div>

               <Row><Col><hr className="my-0" /></Col></Row>
               <br />
               {itemWithDesc !== "" ? <Row><Col><label className="h6 fw-bold my-0">{itemWithDesc}</label></Col></Row> : null}
               {site !== "" ? <Row><Col> <label className="h6 fw-bold my-0">For Site {site}</label></Col></Row> : null}

               {/* Enter Pallet Weight */}
               {showWgt ?
                  <div>
                     <Card className="mobileBlueBgAlt text-white">
                        <CardBody className="mobileBlueBgAlt text-white">
                           <CardTitle style={{ textAlign: "center" }} className="mobileBlueBg text-white my-0 py-0 px-0">Enter Net Weight</CardTitle>
                           <Row><Col><hr className="my-0 text-white" /></Col></Row>
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0" width="85px">
                                       <label className="text-black h6 fw-bold my-0">Barcode:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.barcode}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Po #:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.poNum}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Net Wt:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          name="netWt"
                                          type="number"
                                          className="no-spinner h-100 py-1"
                                          value={model.netWeight}
                                          max={99999.9999}
                                          min={1.0000}
                                          required
                                          onFocus={handleFocus}
                                          onChange={(e) => { onChange("netWeight", e.target.value) }}
                                       />
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </CardBody>
                     </Card>
                     <br />
                     <div>
                        <Row><Col><hr className="my-0" /></Col></Row>
                        {model.netWeight > 0 ?
                           <Row>
                              <Col xs="12">
                                 <Button title="Add" className="shredButton w-100 btn-lg my-3" onClick={() => addPallet()}>
                                    ADD
                                 </Button>
                              </Col>
                           </Row>
                           : null
                        }
                        <Row>
                           <Col xs="12">
                              <Button title="Cancel" className="btn w-100 btn-lg my-3" onClick={() => resetModel()}>
                                 Cancel
                              </Button>
                           </Col>
                        </Row>
                     </div>
                  </div>
                  : null
               }

               {/* Scanned Pallets + Save Button */}
               {pallets ?
                  <div className="filter-header">
                     {pallets.length > 0 ?
                        <Row className="justify-content-center">
                           <Col xs="11">
                              <Button title="Save Request" className="btn w-100 btn-lg my-3" onClick={() => saveRequest()}>
                                 Save Request
                              </Button>
                           </Col>
                        </Row>
                        : null
                     }
                     {pallets.length > 0
                        ? <Row className="justify-content-center">
                           <Col xs="11">
                              <VendorPallet data={pallets} onToggle={onToggle} />
                           </Col>
                        </Row>
                        : <Row className="justify-content-center">
                           <Col className="mobileGrayBg my-0 py-2 px-2 h5" xs="10">
                              <span style={{ color: "black" }}>
                                 No Scanned Pallets
                              </span>
                           </Col>
                        </Row>
                     }
                  </div>
                  : null
               }

               {/* Scan a Barcode */}
               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text.toUpperCase());
                                 setLoaded(false);
                                 setOpen(null);
                                 getLabel();
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }}>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               {/* Manual Barcode Entry */}
               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value.toUpperCase())}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => { setManual(null); }} />
                        <Button children="Cancel" className="button-danger" onClick={() => {
                           setBarcode(null);
                           setManual(null);
                           inputFocus("searchInput");
                        }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
